import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

function toImgUrl(pathOrUrl, backendBase) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
  return `${backendBase}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

export default function GalleryImageModal({
  open,
  onClose,
  onSaved,
  mode = "create",
  item = null,
  backendBase = "",
  token = "",
  notify,
}) {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setFile(null);
      setErr("");
      setPreviewUrl(item?.src ? toImgUrl(item.src, backendBase) : "");
    }
  }, [open, item, backendBase]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = useCallback(
    (e) => {
      const picked = e.target.files?.[0];
      e.target.value = "";

      if (!picked) return;

      if (!picked.type.startsWith("image/")) {
        const msg = "Παρακαλώ επέλεξε αρχείο εικόνας.";
        setErr(msg);
        notify?.("error", msg);
        return;
      }

      setErr("");

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setFile(picked);
      setPreviewUrl(URL.createObjectURL(picked));
    },
    [previewUrl, notify]
  );

  const handleSave = async () => {
    if (!file) {
      const msg = "Δεν έχεις επιλέξει εικόνα.";
      setErr(msg);
      notify?.("error", msg);
      return;
    }

    setSaving(true);
    setErr("");

    try {
      const fd = new FormData();
      fd.append("image", file);

      const url =
        mode === "edit" && item?._id
          ? `${backendBase}/api/admin/images/client/gallery/${item._id}/image`
          : `${backendBase}/api/admin/images/client/gallery/image`;

      const method = mode === "edit" ? "patch" : "post";

      const res = await axios({
        url,
        method,
        data: fd,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const savedItem = res.data?.data || res.data?.item || res.data;
      const message =
        res.data?.message ||
        (mode === "edit" ? "Η εικόνα ενημερώθηκε επιτυχώς." : "Η εικόνα προστέθηκε επιτυχώς.");

      notify?.("success", message);
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("Gallery image save failed:", e);
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Αποτυχία αποθήκευσης εικόνας.";
      setErr(message);
      notify?.("error", message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a2e",
          color: "#fff",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <PhotoCameraIcon sx={{ color: "#fff" }} />
          <Typography fontWeight={900} variant="h6">
            {mode === "edit" ? "Αντικατάσταση Εικόνας" : "Νέα Εικόνα Gallery"}
          </Typography>
        </Stack>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(255,255,255,0.6)" }}
          disabled={saving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mb: 1 }}>
              Επίλεξε αρχείο εικόνας
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff" }}
              >
                Επιλογή αρχείου
              </Button>

              {file && (
                <Chip
                  label={file.name}
                  size="small"
                  onDelete={() => {
                    setFile(null);
                    setPreviewUrl(item?.src ? toImgUrl(item.src, backendBase) : "");
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.6)" },
                  }}
                />
              )}
            </Stack>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Box>

          <Box
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.16)",
              bgcolor: "rgba(255,255,255,0.04)",
              minHeight: 220,
              display: "grid",
              placeItems: "center",
            }}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Preview"
                sx={{
                  width: "100%",
                  height: 360,
                  objectFit: "contain",
                  display: "block",
                  bgcolor: "#111",
                }}
              />
            ) : (
              <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
                Δεν έχει επιλεγεί εικόνα
              </Typography>
            )}
          </Box>

          {err && (
            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
              {err}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
        >
          Ακύρωση
        </Button>

        <Button variant="contained" onClick={handleSave} disabled={!file || saving} sx={{ flex: 1 }}>
          {saving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} color="inherit" />
              <span>Αποθήκευση...</span>
            </Stack>
          ) : mode === "edit" ? (
            "Αντικατάσταση"
          ) : (
            "Αποθήκευση"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
